import { useEffect, useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft, Save } from 'lucide-react';
import { mockKioskSettings } from '../data/mock-kiosk-data';
import { KioskSettings } from '../types';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';

export function KioskSettingsEditPage() {
  const { id } = useParams({ from: '/_authenticated/kiosk/settings/edit/$id' });
  const [settings, setSettings] = useState<KioskSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  
  useEffect(() => {
    // In a real app, this would be an API call
    const foundSettings = mockKioskSettings.find(s => s.id === id);
    if (foundSettings) {
      setSettings({...foundSettings}); // Create a copy to avoid mutating the original
    } else {
      setSettings(null);
    }
    setLoading(false);
  }, [id]);
  
  const handleSave = () => {
    // In a real app, this would be an API call to save the settings
    alert('Settings saved successfully!');
    navigate({ to: '/kiosk/settings/$id', params: { id } });
  };
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  if (!settings) {
    return (
      <div className="space-y-4">
        <PageHeader
          title="Settings Not Found"
          description="The requested kiosk settings could not be found."
          actions={
            <Button asChild variant="outline" size="sm">
              <Link to="/kiosk/settings">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Settings
              </Link>
            </Button>
          }
        />
        <Card>
          <CardContent className="pt-6">
            <p>The kiosk settings with ID "{id}" could not be found.</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <>
      <PageHeader
        title={`Edit ${settings.name}`}
        description="Modify kiosk settings configuration."
        actions={
          <>
            <Button asChild variant="outline" size="sm">
              <Link to="/kiosk/settings/$id" params={{ id }}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Cancel
              </Link>
            </Button>
            <Button size="sm" onClick={handleSave}>
              <Save className="mr-2 h-4 w-4" />
              Save Changes
            </Button>
          </>
        }
      />
      
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>General Settings</CardTitle>
            <CardDescription>
              Basic information about this kiosk settings profile.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input 
                  id="name" 
                  value={settings.name} 
                  onChange={(e) => setSettings({...settings, name: e.target.value})}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="defaultMode">Default Mode</Label>
                <Select 
                  value={settings.defaultMode}
                  onValueChange={(value) => setSettings({...settings, defaultMode: value as any})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select default mode" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rotation">Rotation</SelectItem>
                    <SelectItem value="search">Search</SelectItem>
                    <SelectItem value="browse">Browse</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea 
                id="description" 
                value={settings.description} 
                onChange={(e) => setSettings({...settings, description: e.target.value})}
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <Switch 
                id="active" 
                checked={settings.active}
                onCheckedChange={(checked) => setSettings({...settings, active: checked})}
              />
              <Label htmlFor="active">Active</Label>
            </div>
          </CardContent>
        </Card>
        
        <Tabs defaultValue="rotation" className="space-y-4">
          <TabsList>
            <TabsTrigger value="rotation">Rotation</TabsTrigger>
            <TabsTrigger value="search">Search</TabsTrigger>
            <TabsTrigger value="browse">Browse</TabsTrigger>
            <TabsTrigger value="display">Display</TabsTrigger>
          </TabsList>
          
          <TabsContent value="rotation">
            <Card>
              <CardHeader>
                <CardTitle>Rotation Settings</CardTitle>
                <CardDescription>
                  Configure how documents rotate in the kiosk display.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="interval">Interval (seconds)</Label>
                    <Input 
                      id="interval" 
                      type="number" 
                      min="1"
                      value={settings.rotation.interval} 
                      onChange={(e) => setSettings({
                        ...settings, 
                        rotation: {
                          ...settings.rotation,
                          interval: parseInt(e.target.value)
                        }
                      })}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="transitionEffect">Transition Effect</Label>
                    <Select 
                      value={settings.rotation.transitionEffect}
                      onValueChange={(value) => setSettings({
                        ...settings, 
                        rotation: {
                          ...settings.rotation,
                          transitionEffect: value as any
                        }
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select transition effect" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fade">Fade</SelectItem>
                        <SelectItem value="slide">Slide</SelectItem>
                        <SelectItem value="zoom">Zoom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Switch 
                    id="autoStart" 
                    checked={settings.rotation.autoStart}
                    onCheckedChange={(checked) => setSettings({
                      ...settings, 
                      rotation: {
                        ...settings.rotation,
                        autoStart: checked
                      }
                    })}
                  />
                  <Label htmlFor="autoStart">Auto Start</Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Switch 
                    id="showMetadata" 
                    checked={settings.rotation.showMetadata}
                    onCheckedChange={(checked) => setSettings({
                      ...settings, 
                      rotation: {
                        ...settings.rotation,
                        showMetadata: checked
                      }
                    })}
                  />
                  <Label htmlFor="showMetadata">Show Metadata</Label>
                </div>
                
                <div className="space-y-2">
                  <Label>Documents for Rotation</Label>
                  <p className="text-sm text-muted-foreground">
                    In a real implementation, this would be a document selector.
                  </p>
                  <Textarea 
                    placeholder="doc1, doc2, doc3"
                    value={settings.rotation.documentIds.join(', ')}
                    onChange={(e) => setSettings({
                      ...settings, 
                      rotation: {
                        ...settings.rotation,
                        documentIds: e.target.value.split(',').map(id => id.trim()).filter(id => id)
                      }
                    })}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="search">
            <Card>
              <CardHeader>
                <CardTitle>Search Settings</CardTitle>
                <CardDescription>
                  Configure search functionality for the kiosk.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Switch 
                    id="searchEnabled" 
                    checked={settings.search.enabled}
                    onCheckedChange={(checked) => setSettings({
                      ...settings, 
                      search: {
                        ...settings.search,
                        enabled: checked
                      }
                    })}
                  />
                  <Label htmlFor="searchEnabled">Enable Search</Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Switch 
                    id="autocompleteEnabled" 
                    checked={settings.search.autocompleteEnabled}
                    onCheckedChange={(checked) => setSettings({
                      ...settings, 
                      search: {
                        ...settings.search,
                        autocompleteEnabled: checked
                      }
                    })}
                  />
                  <Label htmlFor="autocompleteEnabled">Enable Autocomplete</Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Switch 
                    id="fuzzyMatchingEnabled" 
                    checked={settings.search.fuzzyMatchingEnabled}
                    onCheckedChange={(checked) => setSettings({
                      ...settings, 
                      search: {
                        ...settings.search,
                        fuzzyMatchingEnabled: checked
                      }
                    })}
                  />
                  <Label htmlFor="fuzzyMatchingEnabled">Enable Fuzzy Matching</Label>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="maxResults">Max Results</Label>
                    <Input 
                      id="maxResults" 
                      type="number" 
                      min="1"
                      value={settings.search.maxResults} 
                      onChange={(e) => setSettings({
                        ...settings, 
                        search: {
                          ...settings.search,
                          maxResults: parseInt(e.target.value)
                        }
                      })}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="defaultSection">Default Section</Label>
                    <Select 
                      value={settings.search.defaultSection}
                      onValueChange={(value) => setSettings({
                        ...settings, 
                        search: {
                          ...settings.search,
                          defaultSection: value as any
                        }
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select default section" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="employees">Employees</SelectItem>
                        <SelectItem value="directors">Directors</SelectItem>
                        <SelectItem value="archives">Archives</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Search Sections</Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="searchEmployees" 
                        checked={settings.search.searchSections.includes('employees')}
                        onCheckedChange={(checked) => {
                          const sections = [...settings.search.searchSections];
                          if (checked) {
                            if (!sections.includes('employees')) {
                              sections.push('employees');
                            }
                          } else {
                            const index = sections.indexOf('employees');
                            if (index !== -1) {
                              sections.splice(index, 1);
                            }
                          }
                          setSettings({
                            ...settings, 
                            search: {
                              ...settings.search,
                              searchSections: sections
                            }
                          });
                        }}
                      />
                      <Label htmlFor="searchEmployees">Employees</Label>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="searchDirectors" 
                        checked={settings.search.searchSections.includes('directors')}
                        onCheckedChange={(checked) => {
                          const sections = [...settings.search.searchSections];
                          if (checked) {
                            if (!sections.includes('directors')) {
                              sections.push('directors');
                            }
                          } else {
                            const index = sections.indexOf('directors');
                            if (index !== -1) {
                              sections.splice(index, 1);
                            }
                          }
                          setSettings({
                            ...settings, 
                            search: {
                              ...settings.search,
                              searchSections: sections
                            }
                          });
                        }}
                      />
                      <Label htmlFor="searchDirectors">Directors</Label>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="searchArchives" 
                        checked={settings.search.searchSections.includes('archives')}
                        onCheckedChange={(checked) => {
                          const sections = [...settings.search.searchSections];
                          if (checked) {
                            if (!sections.includes('archives')) {
                              sections.push('archives');
                            }
                          } else {
                            const index = sections.indexOf('archives');
                            if (index !== -1) {
                              sections.splice(index, 1);
                            }
                          }
                          setSettings({
                            ...settings, 
                            search: {
                              ...settings.search,
                              searchSections: sections
                            }
                          });
                        }}
                      />
                      <Label htmlFor="searchArchives">Archives</Label>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="browse">
            <Card>
              <CardHeader>
                <CardTitle>Browse Settings</CardTitle>
                <CardDescription>
                  Configure browsing functionality for the kiosk.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Switch 
                    id="browseEnabled" 
                    checked={settings.browse.enabled}
                    onCheckedChange={(checked) => setSettings({
                      ...settings, 
                      browse: {
                        ...settings.browse,
                        enabled: checked
                      }
                    })}
                  />
                  <Label htmlFor="browseEnabled">Enable Browse</Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Switch 
                    id="categoriesEnabled" 
                    checked={settings.browse.categoriesEnabled}
                    onCheckedChange={(checked) => setSettings({
                      ...settings, 
                      browse: {
                        ...settings.browse,
                        categoriesEnabled: checked
                      }
                    })}
                  />
                  <Label htmlFor="categoriesEnabled">Enable Categories</Label>
                </div>
                
                <div className="space-y-2">
                  <Label>Browse Sections</Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="browseEmployees" 
                        checked={settings.browse.sections.employees}
                        onCheckedChange={(checked) => setSettings({
                          ...settings, 
                          browse: {
                            ...settings.browse,
                            sections: {
                              ...settings.browse.sections,
                              employees: !!checked
                            }
                          }
                        })}
                      />
                      <Label htmlFor="browseEmployees">Employees</Label>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="browseDirectors" 
                        checked={settings.browse.sections.directors}
                        onCheckedChange={(checked) => setSettings({
                          ...settings, 
                          browse: {
                            ...settings.browse,
                            sections: {
                              ...settings.browse.sections,
                              directors: !!checked
                            }
                          }
                        })}
                      />
                      <Label htmlFor="browseDirectors">Directors</Label>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="browseArchives" 
                        checked={settings.browse.sections.archives}
                        onCheckedChange={(checked) => setSettings({
                          ...settings, 
                          browse: {
                            ...settings.browse,
                            sections: {
                              ...settings.browse.sections,
                              archives: !!checked
                            }
                          }
                        })}
                      />
                      <Label htmlFor="browseArchives">Archives</Label>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Grid Columns</Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="gridMobile">Mobile</Label>
                      <Input 
                        id="gridMobile" 
                        type="number" 
                        min="1"
                        max="4"
                        value={settings.browse.gridColumns.mobile} 
                        onChange={(e) => setSettings({
                          ...settings, 
                          browse: {
                            ...settings.browse,
                            gridColumns: {
                              ...settings.browse.gridColumns,
                              mobile: parseInt(e.target.value)
                            }
                          }
                        })}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="gridTablet">Tablet</Label>
                      <Input 
                        id="gridTablet" 
                        type="number" 
                        min="1"
                        max="6"
                        value={settings.browse.gridColumns.tablet} 
                        onChange={(e) => setSettings({
                          ...settings, 
                          browse: {
                            ...settings.browse,
                            gridColumns: {
                              ...settings.browse.gridColumns,
                              tablet: parseInt(e.target.value)
                            }
                          }
                        })}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="gridDesktop">Desktop</Label>
                      <Input 
                        id="gridDesktop" 
                        type="number" 
                        min="1"
                        max="8"
                        value={settings.browse.gridColumns.desktop} 
                        onChange={(e) => setSettings({
                          ...settings, 
                          browse: {
                            ...settings.browse,
                            gridColumns: {
                              ...settings.browse.gridColumns,
                              desktop: parseInt(e.target.value)
                            }
                          }
                        })}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="display">
            <Card>
              <CardHeader>
                <CardTitle>Display Settings</CardTitle>
                <CardDescription>
                  Configure display options for the kiosk interface.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="theme">Theme</Label>
                    <Select 
                      value={settings.display.theme}
                      onValueChange={(value) => setSettings({
                        ...settings, 
                        display: {
                          ...settings.display,
                          theme: value as any
                        }
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select theme" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="light">Light</SelectItem>
                        <SelectItem value="dark">Dark</SelectItem>
                        <SelectItem value="system">System</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="idleTimeout">Idle Timeout (seconds)</Label>
                    <Input 
                      id="idleTimeout" 
                      type="number" 
                      min="0"
                      value={settings.display.idleTimeout} 
                      onChange={(e) => setSettings({
                        ...settings, 
                        display: {
                          ...settings.display,
                          idleTimeout: parseInt(e.target.value)
                        }
                      })}
                    />
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Switch 
                    id="idleReturnToHome" 
                    checked={settings.display.idleReturnToHome}
                    onCheckedChange={(checked) => setSettings({
                      ...settings, 
                      display: {
                        ...settings.display,
                        idleReturnToHome: checked
                      }
                    })}
                  />
                  <Label htmlFor="idleReturnToHome">Return to Home on Idle</Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Switch 
                    id="showClock" 
                    checked={settings.display.showClock}
                    onCheckedChange={(checked) => setSettings({
                      ...settings, 
                      display: {
                        ...settings.display,
                        showClock: checked
                      }
                    })}
                  />
                  <Label htmlFor="showClock">Show Clock</Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Switch 
                    id="showLogo" 
                    checked={settings.display.showLogo}
                    onCheckedChange={(checked) => setSettings({
                      ...settings, 
                      display: {
                        ...settings.display,
                        showLogo: checked
                      }
                    })}
                  />
                  <Label htmlFor="showLogo">Show Logo</Label>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="customCss">Custom CSS</Label>
                  <Textarea 
                    id="customCss" 
                    value={settings.display.customCss} 
                    onChange={(e) => setSettings({
                      ...settings, 
                      display: {
                        ...settings.display,
                        customCss: e.target.value
                      }
                    })}
                    placeholder="Enter custom CSS here"
                    className="font-mono text-sm"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        
        <div className="flex justify-end">
          <Button size="lg" onClick={handleSave}>
            <Save className="mr-2 h-4 w-4" />
            Save Changes
          </Button>
        </div>
      </div>
    </>
  );
}

export default KioskSettingsEditPage;
